import React, { useState, useCallback } from 'react';
import {
  FTRVoteDecision,
  ScreenVoteDecision,
  Study,
} from 'pages/StudyList/StudyList';
import { createToast, Toast } from 'components/core';

export type AsyncVoteFunction = (
  voteDecision: ScreenVoteDecision,
  consensus: boolean
) => Promise<Study['id']>;

export type FullTextReviewVote = (
  studyId: number,
  voteDecision: FTRVoteDecision,
  exclusionReason?: string
) => Promise<Study['id']>;

type VoteFunctionType = AsyncVoteFunction | FullTextReviewVote;

export const useMakeVote = <T extends VoteFunctionType>(
  asyncFunction: T,
  isPersistent = false
) => {
  const [isVoting, setIsVoting] = useState(false);

  const makeVote = useCallback(
    async (...args: Parameters<T>): Promise<Study['id']> => {
      setIsVoting(true);
      try {
        const voteFunction = asyncFunction as (
          ...a: Parameters<T>
        ) => Promise<Study['id']>;
        const studyId = await voteFunction(...args);
        isPersistent && setIsVoting(false);
        return studyId;
      } catch (error) {
        let errorMessage =
          "Oh no! We're experiencing some technical difficulties right now, please try again later";

        if (error instanceof Error) {
          errorMessage = error.message;
        }

        createToast(
          <Toast type="danger" role="alert">
            {errorMessage}
          </Toast>
        );

        // Unlike a successful vote (which usually removes the study from
        // the current list, unmounting this button), a failed vote leaves
        // the button on screen - it must always recover so the reviewer can
        // retry, regardless of isPersistent.
        setIsVoting(false);

        return 0;
      }
    },
    [asyncFunction, isPersistent]
  );

  return [makeVote, isVoting] as const;
};
